import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HTTPS_REQUEST_OPTIONS } from '@constants/https-request-options/https-request-options';
import { ApiLocationService } from '@services/api-location/api-location.service';
import { RealtimePollingRequestParameters } from '@services/realtime-polling/realtime-polling-request-parameters';
import { interval, Observable, Subject } from 'rxjs';
import { share, startWith, takeUntil, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class RealtimePollingService {
  private DEFAULT_REFRESH_INTERVAL: number = 10; //10 s -> must be integer
  private DEFAULT_SAMPLING_RATE: number = 1; //1 s or 0.5s ..(can be float)
  private DATA_DELAY_OFFSET: number = 30 * 1000;
  private MILLISECONDS_FACTOR: number = 1000;

  private stopPolling: Map<string, Subject<any>>;
  private loadingPromises: Map<string, Observable<any>> = new Map<string, Observable<any>>();
  private displayPromises: Map<string, Observable<any>> = new Map<string, Observable<any>>();
  private dataRefreshPromises: Map<string, Subject<any>> = new Map<string, Subject<any>>();
  private localDataStorage: Map<string, any> = new Map<string, any>();
  private lastReceivedTimestamp: Map<string, number> = new Map<string, number>();

  constructor(private http: HttpClient,
              private apiLocationService: ApiLocationService) {
    this.stopPolling = new Map<string, Subject<any>>();
  }

  public start(url: string, parameters: RealtimePollingRequestParameters, key: string): Observable<any> {
    console.debug('RealTimePollService. Start loading and delivering data for machine', key);
    const displayAllData: boolean = !!(parameters.chartId);
    if (this.invalidParameters(parameters)) {
      console.log('invalid parameters');
      return new Subject();
    }

    const refreshInterval = this.getValidValue(parameters.refresh, this.DEFAULT_REFRESH_INTERVAL);
    const samplingRate = this.getValidValue(parameters.sampling, this.DEFAULT_SAMPLING_RATE);
    let subject = this.stopPolling.get(key + '');

    if (subject === null || subject === undefined) {
      subject = new Subject();
      this.stopPolling.set(key + '', subject);
    }
    const dataRetrievePromise = interval(refreshInterval * this.MILLISECONDS_FACTOR).pipe(startWith(0),
        tap(console.log),
        share(),
        takeUntil(subject));
    this.loadingPromises.set(key, dataRetrievePromise);

    const dataRefreshPromise = new Subject<any>();
    this.dataRefreshPromises.set(key, dataRefreshPromise);
    dataRetrievePromise.subscribe(() => {
      this.loadData(url, parameters, key, displayAllData);
    });

    if (!parameters.latestOnly) {
      const dataDisplayPromise = interval(samplingRate * this.MILLISECONDS_FACTOR).pipe(startWith(0),
          share(),
          takeUntil(subject));
      this.displayPromises.set(key, dataDisplayPromise);
      dataDisplayPromise.subscribe(() => this.deliverData(key, displayAllData, parameters));
    }

    return dataRefreshPromise;

  }

  public stop(key: string) {
    console.debug('RealTimePollService. stop poll requests for key: ' + key);
    if (this.stopPolling.has(key)) {
      this.stopPolling.get(key)!.next(key);
    }
    this.loadingPromises.delete(key);
    this.displayPromises.delete(key);
    this.dataRefreshPromises.delete(key);

    delete this.localDataStorage[key];
    delete this.lastReceivedTimestamp[key];

  }

  public stopAll() {
    const keys = Array.from(this.loadingPromises.keys());
    for (const key of keys) {
      this.stop(key);
    }
  }

  private loadData(url: string, parameters: RealtimePollingRequestParameters, key: string, displayAllData: boolean): void {
    let isFirstCall: boolean = false;
    if (!Array.isArray(this.localDataStorage[key])) {
      this.localDataStorage[key] = [];
      isFirstCall = true;
    }

    parameters = this.updateRequestParameters(key, parameters);

    this.getDataFromBackend(url, parameters).subscribe((success) => {
      const receivedDataset = JSON.parse(JSON.stringify(success));
      console.debug('RealTimePollService. successfully loaded data for key', key, receivedDataset);
      this.storeDataForMachine(key, receivedDataset);
      if (parameters.latestOnly) {
        const latestData = this.localDataStorage[key].shift();
        this.localDataStorage[key] = [];
        if (latestData && this.dataRefreshPromises.has(key)) {
          this.dataRefreshPromises.get(key)!.next(latestData);
        }
        return;
      }
      if (isFirstCall) {
        if (this.localDataStorage && this.localDataStorage[key]) {
          // for line charts on first load we display data for 'n' minutes
          // and on subsequent requests we do the 10 seconds retrieval
          if (displayAllData && isFirstCall) {
            const items = this.localDataStorage[key];
            const firstOldDataPosition = this.getFirstOldDataPosition(items, parameters.refresh);
            const itemsStoredForLoading = this.getItemsStoredForLoading(items, firstOldDataPosition);
            const itemsToInitiallyDisplay = this.localDataStorage[key].slice(firstOldDataPosition, items.length);

            this.dataRefreshPromises.get(key)!.next(itemsToInitiallyDisplay);
            this.localDataStorage[key] = itemsStoredForLoading;
            isFirstCall = false;
          } else {
            const dataToDeliver = this.localDataStorage[key].pop();
            if (this.dataRefreshPromises.has(key)) {
              this.dataRefreshPromises.get(key)!.next(dataToDeliver);
            }
          }
        }
      }
    }, (error) => {
      console.error('RealTimePollService. error loading data from poll service',
          JSON.stringify(error));
      if (this.dataRefreshPromises.has(key)) {
        this.dataRefreshPromises.get(key)!.next(null);
      }
      this.stop(key);
    });
  }

  private updateRequestParameters(key: string, parameters: RealtimePollingRequestParameters): RealtimePollingRequestParameters {
    if ((parameters.timestamp || parameters.timestamp === 0) && this.lastReceivedTimestamp[key]) {
      parameters.timestamp = this.lastReceivedTimestamp[key];
      parameters.initial = false;
    } else {
      //initial load for admin defined charts
      if (parameters.chart && parameters.range) {
        parameters.timestamp = this.lastReceivedTimestamp[key] || 0;
      }
    }

    parameters.refresh = this.getValidValue(parameters.refresh, this.DEFAULT_REFRESH_INTERVAL);
    parameters.sampling = this.getValidValue(parameters.sampling, this.DEFAULT_SAMPLING_RATE);
    return parameters;
  }

  public getDataFromBackend(url: string, parameters: RealtimePollingRequestParameters): Observable<any> {
    const finalUrl = this.apiLocationService.getLocation() + url
    .replace(':tenant', String(parameters.organizationId))
    .replace(':machineId', String(parameters.machineId))
    .replace(':machine', String(parameters.machineId))
    .replace(':userId', String(parameters.firebaseUserId))
    .replace(':widgetId', String(parameters.chartId))
    .replace(':initial', String(parameters.initial))
    .replace(':timestamp', String(parameters.timestamp))
    .replace(':refresh', String(parameters.refresh))
    .replace(':pageId', String(parameters.pageId))
    .replace(':sampling', String(parameters.sampling))
    .replace(':templateWidget', String(parameters.templateWidget));
    return this.http.get(finalUrl, HTTPS_REQUEST_OPTIONS);
  }

  private deliverData(key: string, displayAllData: boolean, parameters: RealtimePollingRequestParameters): void {
    if (!Array.isArray(this.localDataStorage[key])) {
      this.localDataStorage[key] = [];
    }

    if (this.localDataStorage[key].length === 0) {
      return;
    }
    const refreshInterval = this.getValidValue(parameters.refresh, this.DEFAULT_REFRESH_INTERVAL);
    const samplingRate = this.getValidValue(parameters.sampling, this.DEFAULT_SAMPLING_RATE);

    let result: any = [];
    let dataToDeliver: any = this.localDataStorage[key].pop();
    const numberOfItemsToKeep = this.validateItemsNumbersBySamplingAndRefresh(refreshInterval, samplingRate);
    while (this.localDataStorage[key].length > numberOfItemsToKeep) {
      const nextDataToDeliver = this.localDataStorage[key].pop();
      if (this.isDataWithinOffset(nextDataToDeliver.ts, this.DATA_DELAY_OFFSET)) {
        console.debug('RealTimePollService. skip element because it is within offset', this.DATA_DELAY_OFFSET);
        this.localDataStorage[key].push(nextDataToDeliver);
        break;
      }
      if (displayAllData) {
        result.push(dataToDeliver);
      }
      dataToDeliver = nextDataToDeliver;
    }

    if (displayAllData) {
      result.push(dataToDeliver);
    } else {
      result = dataToDeliver;
    }
    this.dataRefreshPromises.get(key)!.next(result);
  }

  private validateItemsNumbersBySamplingAndRefresh(refreshInterval, sampling) {
    if (!sampling || sampling <= 0) {
      sampling = 1;
    }

    if (!refreshInterval || refreshInterval <= 0) {
      refreshInterval = 10;
    }

    return Math.round(parseFloat(refreshInterval) / parseFloat(sampling));

  }

  private getValidValue(value: number | null, defaultValue: number): number {
    if (value === null || undefined === value || isNaN(value)) {
      return defaultValue;
    }
    const parsedNumber = parseFloat(String(value));
    if (isNaN(parsedNumber) || parsedNumber <= 0) {
      return defaultValue;
    }

    return value;
  }

  private storeDataForMachine(key: string, data: any) {
    if (!Array.isArray(data)) {
      if (this.localDataStorage[key]) {
        this.localDataStorage[key].unshift(data);
      }
    } else {
      for (const singleResponse of data) {
        if (this.datasetOfMachineAlreadyReceived(key, singleResponse)) {
          console.debug('RealTimePollService. discard already received data object ', singleResponse);
        } else {
          if (this.localDataStorage[key]) {
            this.localDataStorage[key].unshift(singleResponse);
          }
        }
      }
    }
  }

  private datasetOfMachineAlreadyReceived(key, dataset) {
    if (!this.lastReceivedTimestamp[key]) {
      this.lastReceivedTimestamp[key] = 0;
    }

    if (this.lastReceivedTimestamp[key] < dataset.ts) {
      this.lastReceivedTimestamp[key] = dataset.ts;
      return false;
    }
    return true;
  }

  private getItemsStoredForLoading(items, oldDataPosition) {
    const result = items.slice(0, oldDataPosition);
    result.sort(function (a, b) {
      return b.ts - a.ts;
    });
    return result;
  }

  private getFirstOldDataPosition(items, refreshInterval) {

    for (let i = 0; i < items.length; i++) {
      const offset = this.DATA_DELAY_OFFSET + (refreshInterval * 1000);
      if (!this.isDataWithinOffset(items[i].ts, offset)) {
        return i + 1;
      }
    }
    return items.length || 0;

  }

  private isDataWithinOffset(timestamp, offset) {
    const dataOffset = new Date().getTime() - timestamp;
    return (dataOffset < offset);
  }

  private invalidParameters(parameters: RealtimePollingRequestParameters): boolean {
    if (parameters === null || parameters === undefined) {
      return true;
    }
    if (parameters.machineId === undefined || parameters.machineId <= 0) {
      return true;
    }
    if (parameters.organizationId === undefined || parameters.organizationId <= 0) {
      return true;
    }

    return false;
  }
}
